import { z } from "zod";

/**
 * One schema per wizard step (`/sell/media`, `/sell/details`,
 * `/sell/finalize`) — each step's own `useForm` validates only its slice of
 * `ListingDraft`, matching that step's own required fields per
 * `docs/ui/routes.md`'s 3-step reconciliation. Numeric fields stay
 * `z.string()` here (not `z.coerce.number()`): every field in `ListingDraft`
 * is a native input's string value, so the schema validates the same shape
 * the form actually holds — conversion to a real number happens once, at
 * submit time (`toListingFormData`).
 */
export const mediaStepSchema = z.object({
  images: z
    .array(z.instanceof(File))
    .min(1, "Add at least one photo.")
    .max(8, "You can add up to 8 photos."),
});
export type MediaStepValues = z.infer<typeof mediaStepSchema>;

const conditionEnum = z.enum(["new", "like-new", "used", "for-parts"]);

export const detailsStepSchema = z.object({
  title: z.string().trim().min(5, "Title must be at least 5 characters").max(120),
  description: z.string().trim().min(20, "Description must be at least 20 characters").max(3000),
  categorySlug: z.string().trim().min(1, "Choose a category"),
  condition: conditionEnum,
  brand: z.string().trim().max(60).optional(),
});
export type DetailsStepValues = z.infer<typeof detailsStepSchema>;

export const finalizeStepSchema = z
  .object({
    price: z
      .string()
      .trim()
      .min(1, "Enter a price")
      .refine((value) => Number(value) > 0, "Enter a price greater than 0"),
    originalPrice: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || Number(value) > 0, "Enter a valid price"),
    negotiable: z.boolean(),
    city: z.string().trim().min(1, "City is required"),
    state: z.string().trim().min(1, "State is required"),
    tags: z.string().trim().optional(),
  })
  .refine((data) => !data.originalPrice || Number(data.originalPrice) > Number(data.price), {
    message: "Original price must be greater than the asking price",
    path: ["originalPrice"],
  });
export type FinalizeStepValues = z.infer<typeof finalizeStepSchema>;
